import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Addenda are their own document type, not `ProgressNote`. Two things depend
 * on that:
 *   - the Health Gorilla subscription matches `type=ProgressNote`, so a
 *     distinct code keeps addenda internal by construction rather than by a
 *     gate someone can forget;
 *   - the practitioner AccessPolicy scopes writes by type.
 *
 * The display deliberately avoids the words "progress note":
 * `assertProgressNoteDocument` accepts any document whose type display
 * contains them, and an addendum must not pass that check.
 */
export const ADDENDUM_DOC_TYPE_SYSTEM = 'https://behuman.health/document-type'
export const ADDENDUM_DOC_TYPE_CODE = 'ProgressNoteAddendum'
export const ADDENDUM_DOC_TYPE_DISPLAY = 'Addendum'
export const ADDENDUM_REASON_EXTENSION_URL = 'https://behuman.health/addendum-reason'

export const addendumReasonSchema = z.enum([
  'correction',
  'clarification',
  'additional-information',
  'other',
])
export type AddendumReason = z.infer<typeof addendumReasonSchema>

export const saveAddendumResponseSchema = z.object({
  addendumId: z.string(),
})
export type SaveAddendumResponse = z.infer<typeof saveAddendumResponseSchema>

export const signAddendumResponseSchema = z.object({
  addendumId: z.string(),
  signedAt: z.string(),
  alreadySigned: z.boolean(),
})
export type SignAddendumResponse = z.infer<typeof signAddendumResponseSchema>

/** Creates the draft addendum, or updates one that has not been signed yet. */
export const progressNoteSaveAddendum = oc
  .route({ method: 'POST', path: '/v2/progress-notes/{documentReferenceId}/addenda' })
  .input(
    z.object({
      documentReferenceId: z.string().min(1),
      addendumId: z.string().min(1).optional(),
      content: z.string().trim().min(1),
      reason: addendumReasonSchema,
    }),
  )
  .output(saveAddendumResponseSchema)

/**
 * Not nested under `/progress-notes/{id}/…`: a sibling path keeps the addendum
 * id from ever being read as a document id.
 */
export const progressNoteSignAddendum = oc
  .route({ method: 'POST', path: '/v2/progress-note-addenda/{addendumId}/sign' })
  .input(z.object({ addendumId: z.string().min(1), attested: z.boolean() }))
  .output(signAddendumResponseSchema)
